import { 
  collection, 
  addDoc, 
  getDocs, 
  query, 
  where, 
  orderBy, 
  serverTimestamp,
  doc,
  updateDoc
} from 'firebase/firestore';
import { db } from '../config/firebase';
import { sanitizeCentralProjectData, sanitizeInstallmentData, sanitizeUCData } from '../utils/centralValidation';

// Central service for handling all central project Firebase operations
export const centralService = {
  
  // Submit a new central project (DoNER/NEC) - handles work order fields
  async submitCentralProject(projectData, userId, projectType) {
    try {
      const sanitizedData = sanitizeCentralProjectData(projectData);
      
      // Add metadata
      const projectToSubmit = {
        ...sanitizedData,
        type: projectType,
        status: 'pending',
        remarks: '', // Initialize empty remarks field
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        createdBy: userId
      };
      
      const docRef = await addDoc(collection(db, 'centralProjects'), projectToSubmit);
      console.log('Central project submitted with ID:', docRef.id);
      
      return { 
        success: true, 
        data: { id: docRef.id, ...projectToSubmit } 
      };
    } catch (error) {
      console.error('Error submitting central project:', error);
      throw new Error('Failed to submit project. Please try again.');
    }
  },

  // UPDATED: Submit a new installment - removed amountReleasedBy field handling
  async submitInstallment(installmentData, userId) {
    try {
      const sanitizedData = sanitizeInstallmentData(installmentData);
      
      // Add metadata
      const installmentToSubmit = {
        ...sanitizedData,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        createdBy: userId
      };
      
      const docRef = await addDoc(collection(db, 'installments'), installmentToSubmit);
      console.log('Installment submitted with ID:', docRef.id);
      
      return { 
        success: true, 
        data: { id: docRef.id, ...installmentToSubmit } 
      };
    } catch (error) {
      console.error('Error submitting installment:', error);
      throw new Error('Failed to submit installment. Please try again.');
    }
  },

  // Submit a new UC
  async submitUC(ucData, userId) {
    try {
      const sanitizedData = sanitizeUCData(ucData);
      
      // Add metadata
      const ucToSubmit = {
        ...sanitizedData,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        createdBy: userId
      };
      
      const docRef = await addDoc(collection(db, 'utilizationCertificates'), ucToSubmit);
      console.log('UC submitted with ID:', docRef.id);
      
      return { 
        success: true, 
        data: { id: docRef.id, ...ucToSubmit } 
      };
    } catch (error) {
      console.error('Error submitting UC:', error);
      throw new Error('Failed to submit UC. Please try again.');
    }
  },

  // Update project remarks
  async updateProjectRemarks(projectId, remarks, userId) {
    try {
      const projectRef = doc(db, 'centralProjects', projectId);
      
      const updateData = {
        remarks: remarks || '',
        updatedAt: serverTimestamp(),
        updatedBy: userId
      };
      
      await updateDoc(projectRef, updateData);
      
      console.log('Project remarks updated:', projectId);
      return { success: true, data: updateData };
    } catch (error) {
      console.error('Error updating project remarks:', error);
      throw new Error('Failed to update project remarks. Please try again.');
    }
  },

  // Get all central projects - handle work order date fields
  async getAllCentralProjects(filters = {}) {
    try {
      let projectsQuery = collection(db, 'centralProjects');
      
      const constraints = [];
      
      // Apply filters
      if (filters.type) {
        constraints.push(where('type', '==', filters.type));
      }
      
      if (filters.scheme) {
        constraints.push(where('scheme', '==', filters.scheme));
      }
      
      if (filters.status) {
        constraints.push(where('status', '==', filters.status));
      }
      
      // Default sort by creation date
      constraints.push(orderBy('createdAt', 'desc'));
      
      if (constraints.length > 0) {
        projectsQuery = query(projectsQuery, ...constraints);
      }
      
      const querySnapshot = await getDocs(projectsQuery);
      const projects = [];
      
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        projects.push({
          id: doc.id,
          ...data,
          createdAt: data.createdAt?.toDate(),
          updatedAt: data.updatedAt?.toDate(),
          workOrderIssueDate: data.workOrderIssueDate?.toDate(),
          scheduledCompletionDate: data.scheduledCompletionDate?.toDate()
        });
      });
      
      return { success: true, data: projects };
    } catch (error) {
      console.error('Error fetching central projects:', error);
      throw new Error('Failed to load projects. Please try again.');
    }
  },

  // Get central projects for dropdown (only submitted projects)
  async getCentralProjectsForDropdown(projectType = null) {
    try {
      // Get all projects first, then filter and sort in memory to avoid index issues
      const projectsQuery = query(collection(db, 'centralProjects'));
      const querySnapshot = await getDocs(projectsQuery);
      
      let projects = [];
      
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        
        // Filter by project type if specified
        if (!projectType || data.type === projectType) {
          projects.push({
            value: doc.id,
            label: data.projectName,
            type: data.type,
            financialYear: data.financialYear,
            scheme: data.scheme
          });
        }
      });
      
      // Sort by project name in memory
      projects.sort((a, b) => a.label.localeCompare(b.label));
      
      return { success: true, data: projects };
    } catch (error) {
      console.error('Error fetching projects for dropdown:', error);
      throw new Error('Failed to load projects. Please try again.');
    }
  },

  // Get all installments - removed amountReleasedBy field from response
  async getAllInstallments(filters = {}) {
    try {
      let installmentsQuery = collection(db, 'installments');
      const constraints = [];
      
      // Apply filters
      if (filters.projectType) {
        constraints.push(where('projectType', '==', filters.projectType));
      }
      
      if (filters.projectId) {
        constraints.push(where('projectId', '==', filters.projectId));
      }
      
      if (filters.financialYear) {
        constraints.push(where('financialYear', '==', filters.financialYear));
      }
      
      // Default sort by creation date
      constraints.push(orderBy('createdAt', 'desc'));
      
      if (constraints.length > 0) {
        installmentsQuery = query(installmentsQuery, ...constraints);
      }
      
      const querySnapshot = await getDocs(installmentsQuery);
      const installments = [];
      
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        installments.push({
          id: doc.id,
          ...data,
          createdAt: data.createdAt?.toDate(),
          updatedAt: data.updatedAt?.toDate(),
          releaseDate: data.releaseDate?.toDate()
        });
      });
      
      return { success: true, data: installments };
    } catch (error) {
      console.error('Error fetching installments:', error);
      throw new Error('Failed to load installments. Please try again.');
    }
  },

  // Get central projects by user - handle work order date fields
  async getCentralProjectsByUser(userId) {
    try {
      const projectsQuery = query(
        collection(db, 'centralProjects'),
        where('createdBy', '==', userId),
        orderBy('createdAt', 'desc')
      );
      
      const querySnapshot = await getDocs(projectsQuery);
      const projects = [];
      
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        projects.push({
          id: doc.id,
          ...data,
          createdAt: data.createdAt?.toDate(),
          updatedAt: data.updatedAt?.toDate(),
          workOrderIssueDate: data.workOrderIssueDate?.toDate(),
          scheduledCompletionDate: data.scheduledCompletionDate?.toDate()
        });
      });
      
      return { success: true, data: projects };
    } catch (error) {
      console.error('Error fetching user central projects:', error);
      throw new Error('Failed to load your projects. Please try again.');
    }
  },

  // Get installments by user
  async getInstallmentsByUser(userId) {
    try {
      const installmentsQuery = query(
        collection(db, 'installments'),
        where('createdBy', '==', userId),
        orderBy('createdAt', 'desc')
      );
      
      const querySnapshot = await getDocs(installmentsQuery);
      const installments = [];
      
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        installments.push({
          id: doc.id,
          ...data,
          createdAt: data.createdAt?.toDate(),
          updatedAt: data.updatedAt?.toDate(),
          releaseDate: data.releaseDate?.toDate()
        });
      });
      
      return { success: true, data: installments };
    } catch (error) {
      console.error('Error fetching user installments:', error);
      throw new Error('Failed to load your installments. Please try again.');
    }
  },

  // Get central projects by type - handle work order date fields
  async getCentralProjectsByType(projectType) {
    try {
      const projectsQuery = query(
        collection(db, 'centralProjects'),
        where('type', '==', projectType),
        orderBy('createdAt', 'desc')
      );
      
      const querySnapshot = await getDocs(projectsQuery);
      const projects = [];
      
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        projects.push({
          id: doc.id,
          ...data,
          createdAt: data.createdAt?.toDate(),
          updatedAt: data.updatedAt?.toDate(),
          workOrderIssueDate: data.workOrderIssueDate?.toDate(),
          scheduledCompletionDate: data.scheduledCompletionDate?.toDate()
        });
      });
      
      return { success: true, data: projects };
    } catch (error) {
      console.error('Error fetching projects by type:', error);
      throw new Error('Failed to load projects. Please try again.');
    }
  },

  // Get installments by project type
  async getInstallmentsByProjectType(projectType) {
    try {
      const installmentsQuery = query(
        collection(db, 'installments'),
        where('projectType', '==', projectType),
        orderBy('createdAt', 'desc')
      );
      
      const querySnapshot = await getDocs(installmentsQuery);
      const installments = [];
      
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        installments.push({
          id: doc.id,
          ...data,
          createdAt: data.createdAt?.toDate(),
          updatedAt: data.updatedAt?.toDate(),
          releaseDate: data.releaseDate?.toDate()
        });
      });
      
      return { success: true, data: installments };
    } catch (error) {
      console.error('Error fetching installments by type:', error);
      throw new Error('Failed to load installments. Please try again.');
    }
  },

  // Get projects for state installments (both DoNER and NEC)
  async getCentralProjectsForStateInstallments() {
    try {
      // Get all projects and sort in memory to avoid index issues
      const projectsQuery = query(collection(db, 'centralProjects'));
      const querySnapshot = await getDocs(projectsQuery);
      
      let projects = [];
      
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        projects.push({
          value: doc.id,
          label: `${data.projectName} (${data.type.toUpperCase()})`,
          type: data.type,
          financialYear: data.financialYear,
          scheme: data.scheme
        });
      });
      
      // Sort by project name in memory
      projects.sort((a, b) => a.label.localeCompare(b.label));
      
      return { success: true, data: projects };
    } catch (error) {
      console.error('Error fetching projects for state installments:', error);
      throw new Error('Failed to load projects. Please try again.');
    }
  },

  // Get UCs by project ID - handle UC certificate date
  async getUCsByProjectId(projectId) {
    try {
      // Get all UCs and filter in memory to avoid index issues
      const ucsQuery = query(collection(db, 'utilizationCertificates'));
      const querySnapshot = await getDocs(ucsQuery);
      
      let ucs = [];
      
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        
        // Filter by project ID
        if (data.projectId === projectId) {
          ucs.push({
            id: doc.id,
            ...data,
            createdAt: data.createdAt?.toDate(),
            updatedAt: data.updatedAt?.toDate(),
            ucCertificateDate: data.ucCertificateDate?.toDate()
          });
        }
      });
      
      // Sort by creation date in memory (newest first)
      ucs.sort((a, b) => b.createdAt - a.createdAt);
      
      return { success: true, data: ucs };
    } catch (error) {
      console.error('Error fetching UCs by project:', error);
      throw new Error('Failed to load UCs. Please try again.');
    }
  },

  // Get all UCs with filters - handle UC certificate date
  async getAllUCs(filters = {}) {
    try {
      // Get all UCs and filter in memory to avoid index issues
      const ucsQuery = query(collection(db, 'utilizationCertificates'));
      const querySnapshot = await getDocs(ucsQuery);
      
      let ucs = [];
      
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        
        // Filter by project type if specified
        if (!filters.projectType || data.projectType === filters.projectType) {
          // Filter by financial year if specified
          if (!filters.financialYear || data.financialYear === filters.financialYear) {
            ucs.push({
              id: doc.id,
              ...data,
              createdAt: data.createdAt?.toDate(),
              updatedAt: data.updatedAt?.toDate(),
              ucCertificateDate: data.ucCertificateDate?.toDate()
            });
          }
        }
      });
      
      // Sort by creation date in memory
      ucs.sort((a, b) => b.createdAt - a.createdAt);
      
      return { success: true, data: ucs };
    } catch (error) {
      console.error('Error fetching all UCs:', error);
      throw new Error('Failed to load UCs. Please try again.');
    }
  },

  // Get project with central share for UC forms
  async getProjectWithCentralShare(projectId) {
    try {
      const projectsQuery = query(collection(db, 'centralProjects'));
      const querySnapshot = await getDocs(projectsQuery);
      
      let project = null;
      
      querySnapshot.forEach((doc) => {
        if (doc.id === projectId) {
          const data = doc.data();
          project = {
            id: doc.id,
            projectName: data.projectName,
            type: data.type,
            centralShare: data.centralShare,
            scheme: data.scheme,
            financialYear: data.financialYear,
            workOrderNumber: data.workOrderNumber,
            workOrderIssueDate: data.workOrderIssueDate?.toDate(),
            scheduledCompletionDate: data.scheduledCompletionDate?.toDate()
          };
        }
      });
      
      if (!project) {
        throw new Error('Project not found');
      }
      
      return { success: true, data: project };
    } catch (error) {
      console.error('Error fetching project with central share:', error);
      throw new Error('Failed to load project details. Please try again.');
    }
  }
};

export default centralService;